import React from 'react';
import ConditionalBubble from '@/components/ConditionalBubble';
import Bubble from '@/v3/components/Bubble';
import StyleTag from '@/components/StyleTag';
import LauncherPreview from '@/v3/components/UI/LauncherPreview';
import useLauncherPreview from '@/hooks/useLauncherPreview';
import { COMMON_CLASSNAMES } from '@/constants';
import useLayoutStore from '@/stores/layout/layoutStore';
import useIsAboveBreakpoint from '@/helpers/useIsAboveBreakpoint';
import clsx from 'clsx';
import useStepFlowStore from '@/stores/stepFlow/stepFlowStore';
import { CHATBOT_ONBOARDING_WORKFLOW_KEY } from '@/stores/stepFlow/chatbotOnboardingSteps';
import {
  CHATBOT_DETAILS_TEST_PREVIEW_NAMESPACE,
  mergeTestPreviewInitialMessages,
} from '@/helpers/chatbotDetailsTestPreview';

/**
 * BubblePreviewWrapper - A reusable component for rendering bubble previews in RightSidebar
 * This component handles the common pattern of showing a bubble preview with launcher
 * 
 * @param {Object} props.config - Bubble configuration object (includes new_bubble_accepted)
 * @param {Object} props.customizationData - Customization data for StyleTag
 * @param {Object} props.bubbleProps - Additional props to pass to the Bubble component
 * @param {boolean} props.showLauncher - Whether to show the launcher preview (default: true)
 * @param {Object} props.launcherProps - Additional props for launcher preview
 * @param {boolean} props.useIsolatedTestPreview - Use a dedicated conversation namespace and demo messages (chatbot detail sidebars)
 */
const BubblePreviewWrapper = ({
  config,
  customizationData,
  bubbleProps = {},
  showLauncher = true,
  launcherProps = {},
  launcherIcon,
  launcherBackground,
  launcherSize,
  applyBannerOffset = true,
  children,
  useIsolatedTestPreview = false,
}) => {
  const bannerHeight = useLayoutStore((state) => state.bannerHeight);
  const isOnboardingActive = useStepFlowStore((state) =>
    Object.values(state.flows).some(
      (flow) => flow.workflowKey === CHATBOT_ONBOARDING_WORKFLOW_KEY && flow.currentStepId !== null
    )
  );
  const isAboveSm = useIsAboveBreakpoint('sm');
  const isAbovePreview = useIsAboveBreakpoint('preview');
  const defaultLauncherProps = useLauncherPreview({
    showBadge: true,
    badgeCount: 1,
    ...launcherProps
  });

  // Always show launcher if showLauncher is true
  const shouldShowLauncher = showLauncher;

  const resolvedConfig =
    useIsolatedTestPreview && config
      ? {
          ...config,
          initial_messages: mergeTestPreviewInitialMessages(
            config.initial_messages ?? customizationData?.initial_messages ?? []
          ),
        }
      : config;

  const resolvedBubbleProps =
    useIsolatedTestPreview
      ? {
          ...bubbleProps,
          conversationNamespace: CHATBOT_DETAILS_TEST_PREVIEW_NAMESPACE,
          isolateTestPreview: true,
        }
      : bubbleProps;

  // Render Bubble directly on mobile and tablet (below preview breakpoint, 1100px)
  // This ensures it fits the screen height properly
  const shouldRenderDirectly = !isAbovePreview;

  if (shouldRenderDirectly) {
    return (
      <>
        <StyleTag tag=".bubble" tempCustomizationData={customizationData || resolvedConfig} />
        <Bubble
          config={resolvedConfig}
          {...resolvedBubbleProps}
        />
      </>
    );
  }

  // Desktop: Use the wrapper with preview styling
  // Base classes without height constraint
  const baseClasses = COMMON_CLASSNAMES.previewBubble.replace('h-[min(728px,calc(100dvh-180px))]', '').trim();
  
  // Responsive height logic:
  // - When height > 850px: bubble is exactly 728px, parent sizes to bubble + launcher
  // - When height <= 850px: dynamically shrink to fit available space
  const heightClass = shouldShowLauncher 
    ? 'h-full max-h-[calc(100vh-100px)] bubble-preview-min-height:h-auto' 
    : 'h-full max-h-[calc(100vh-120px)] bubble-preview-min-height:h-auto';

  return (
    <div
      className={clsx(
        baseClasses,
        heightClass,
        'relative flex flex-col',
        'sm:w-full sm:max-w-[424px]'
      )}
      style={applyBannerOffset && bannerHeight > 0 ? { height: `calc(100% - ${bannerHeight}px)`, marginTop: `${bannerHeight}px` } : undefined}
    >
      <StyleTag tag=".bubble" tempCustomizationData={customizationData || resolvedConfig} />
      <div className={clsx(
        'w-full overflow-hidden',
        // When height > 850px: fixed height 728px, otherwise flex-1 to shrink dynamically
        'flex-1 min-h-0 bubble-preview-min-height:flex-none',
        'bubble-preview-min-height:h-[728px]',
        isOnboardingActive && 'onboarding-bubble-min-height:h-[574px]',
      )}>
        <ConditionalBubble
          config={resolvedConfig}
          {...resolvedBubbleProps}
        />
      </div>
      {shouldShowLauncher && (
        <LauncherPreview
          {...defaultLauncherProps}
          launcherBackground={launcherBackground || config?.launcher_background_color || config?.launcher_background}
          launcherIcon={launcherIcon || config?.launcher_icon}
          launcherSize={launcherSize || config?.bubble_size}
        />
      )}
      {/* {children} */}
    </div>
  );
};

export default BubblePreviewWrapper;

